import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNotify, useRefresh } from 'react-admin';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tenantService } from '../services/tenantService';

// Document type enum matching backend
const DOCUMENT_TYPES = [
  { value: 'INSPECTION_REPORT', label: 'Inspection Report', color: '#0288d1' },
  { value: 'INSURANCE', label: 'Insurance', color: '#ed6c02' },
  { value: 'LEASE_AGREEMENT', label: 'Lease Agreement', color: '#f57c00' },
  { value: 'OTHER', label: 'Other', color: '#757575' },
  { value: 'OWNERSHIP_PERMIT', label: 'Ownership Permit', color: '#1976d2' },
  { value: 'PURCHASE_AGREEMENT', label: 'Purchase Agreement', color: '#7b1fa2' },
  { value: 'REGISTRATION', label: 'Registration', color: '#2e7d32' },
  { value: 'SAFETY_CERTIFICATE', label: 'Safety Certificate', color: '#d32f2f' },
  { value: 'SERVICE_CONTRACT', label: 'Service Contract', color: '#c62828' },
  { value: 'WARRANTY', label: 'Warranty', color: '#388e3c' },
  { value: 'WEIGHT_RATING', label: 'Weight Rating Certificate', color: '#9c27b0' },
];

interface EquipmentDocument {
  id: number;
  documentType: string;
  documentName?: string;
  notes?: string;
  fileId: number;
  issueDate?: string;
  expiryDate?: string;
  file: {
    id: number;
    name: string;
    url: string;
    size?: number;
    mimeType?: string;
  };
}

interface EquipmentDocumentsProps {
  equipmentId: number;
  readOnly?: boolean;
}

export const EquipmentDocuments: React.FC<EquipmentDocumentsProps> = ({ 
  equipmentId, 
  readOnly = false 
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [documents, setDocuments] = useState<EquipmentDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<EquipmentDocument | null>(null);
  const [uploading, setUploading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<EquipmentDocument | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    documentType: '',
    documentName: '',
    notes: '',
    fileId: null as number | null,
    file: null as File | null,
    issueDate: null as Date | null,
    expiryDate: null as Date | null,
  });

  // Fetch documents
  const fetchDocuments = async () => {
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();
      
      const response = await fetch(`${baseUrl}/equipment/${equipmentId}/documents`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      notify('Failed to load documents', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [equipmentId]);

  // Generate unique filename
  const generateUniqueFilename = (originalName: string): string => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop();
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
    return `${nameWithoutExt}_${timestamp}_${randomStr}.${extension}`;
  };

  // Upload file
  const uploadFile = async (file: File): Promise<any> => {
    const uniqueFilename = generateUniqueFilename(file.name);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', uniqueFilename);
    formData.append('folder', 'equipment-documents');
    formData.append('isPublic', 'true');

    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');
    const tenantCode = tenantService.getCurrentTenantCode();

    const response = await fetch(`${baseUrl}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'x-tenant-code': tenantCode,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.documentType) {
      notify('Please select a document type', { type: 'error' });
      return;
    }

    if (!editingDocument && !formData.file) {
      notify('Please select a file to upload', { type: 'error' });
      return;
    }

    setUploading(true);
    
    try {
      let fileId = formData.fileId;
      
      // Upload file if new file selected
      if (formData.file) {
        const uploadedFile = await uploadFile(formData.file);
        fileId = uploadedFile.id;
      }

      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const documentData = {
        documentType: formData.documentType,
        documentName: formData.documentName || undefined,
        notes: formData.notes || undefined,
        fileId: fileId,
        issueDate: formData.issueDate?.toISOString() || undefined,
        expiryDate: formData.expiryDate?.toISOString() || undefined,
      };

      let response;
      if (editingDocument) {
        // Update existing document
        response = await fetch(`${baseUrl}/equipment/documents/${editingDocument.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'x-tenant-code': tenantCode,
          },
          body: JSON.stringify(documentData),
        });
      } else {
        // Create new document
        response = await fetch(`${baseUrl}/equipment/${equipmentId}/documents`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'x-tenant-code': tenantCode,
          },
          body: JSON.stringify(documentData),
        });
      }

      if (response.ok) {
        notify(editingDocument ? 'Document updated successfully' : 'Document added successfully', { type: 'success' });
        handleCloseDialog();
        fetchDocuments();
        refresh();
      } else {
        throw new Error('Failed to save document');
      }
    } catch (error: any) {
      notify(`Failed to save document: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async (documentId: number) => {
    if (!confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const response = await fetch(`${baseUrl}/equipment/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });

      if (response.ok) {
        notify('Document deleted successfully', { type: 'success' });
        fetchDocuments();
        refresh();
      } else {
        throw new Error('Failed to delete document');
      }
    } catch (error: any) {
      notify(`Failed to delete document: ${error.message}`, { type: 'error' });
    }
  };

  // Handle dialog open/close
  const handleOpenDialog = (document?: EquipmentDocument) => {
    if (document) {
      setEditingDocument(document);
      setFormData({
        documentType: document.documentType,
        documentName: document.documentName || '',
        notes: document.notes || '',
        fileId: document.fileId,
        file: null,
        issueDate: document.issueDate ? new Date(document.issueDate) : null,
        expiryDate: document.expiryDate ? new Date(document.expiryDate) : null,
      });
    } else {
      setEditingDocument(null);
      setFormData({
        documentType: '',
        documentName: '',
        notes: '',
        fileId: null,
        file: null,
        issueDate: null,
        expiryDate: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDocument(null);
    setFormData({
      documentType: '',
      documentName: '',
      notes: '',
      fileId: null,
      file: null,
      issueDate: null,
      expiryDate: null,
    });
  };

  // Handle view document
  const handleViewDocument = (doc: EquipmentDocument) => {
    setViewingDocument(doc);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingDocument(null);
  };

  const getDocumentTypeInfo = (type: string) => {
    return DOCUMENT_TYPES.find(dt => dt.value === type) || { label: type, color: '#757575' };
  };

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const expiry = new Date(expiryDate);
    return expiry > new Date() && expiry < thirtyDaysFromNow;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" color="primary">
            Documents
          </Typography>
          {!readOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="small"
            >
              Add Document
            </Button>
          )}
        </Box>

        {documents.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            p: 4, 
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <FileIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No documents uploaded yet
            </Typography>
            {!readOnly && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Add First Document
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Document Name</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => {
                  const typeInfo = getDocumentTypeInfo(doc.documentType);
                  const expired = isExpired(doc.expiryDate);
                  const expiringSoon = isExpiringSoon(doc.expiryDate);
                  
                  return (
                    <TableRow 
                      key={doc.id}
                      sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        borderLeft: expired ? '4px solid #d32f2f' : expiringSoon ? '4px solid #ed6c02' : 'none',
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={typeInfo.label}
                          size="small"
                          sx={{
                            backgroundColor: `${typeInfo.color}20`,
                            color: typeInfo.color,
                            border: `1px solid ${typeInfo.color}40`,
                            minWidth: 120,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {doc.documentName || doc.file.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {doc.notes ? (
                          <Tooltip title={doc.notes}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {doc.notes}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {doc.issueDate ? new Date(doc.issueDate).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {doc.expiryDate ? (
                          <Box>
                            <Typography 
                              variant="body2"
                              color={expired ? 'error' : expiringSoon ? 'warning.main' : 'text.primary'}
                            >
                              {new Date(doc.expiryDate).toLocaleDateString()}
                            </Typography>
                            {expired && (
                              <Chip label="EXPIRED" color="error" size="small" sx={{ mt: 0.5 }} />
                            )}
                            {expiringSoon && !expired && (
                              <Chip label="Expiring Soon" color="warning" size="small" sx={{ mt: 0.5 }} />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {expired ? (
                          <Chip label="Expired" color="error" size="small" />
                        ) : expiringSoon ? (
                          <Chip label="Expiring" color="warning" size="small" />
                        ) : (
                          <Chip label="Active" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="View">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewDocument(doc)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton 
                              size="small" 
                              onClick={() => window.open(doc.file.url, '_blank')}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!readOnly && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleOpenDialog(doc)}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDelete(doc.id)} 
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {editingDocument ? 'Edit Document' : 'Add New Document'}
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={formData.documentType}
                  onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}
                  label="Document Type"
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: type.color,
                          }}
                        />
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Document Name"
                value={formData.documentName}
                onChange={(e) => setFormData({ ...formData, documentName: e.target.value })}
                fullWidth
                helperText="Optional - defaults to file name"
              />

              <TextField
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label="Issue Date"
                    value={formData.issueDate}
                    onChange={(date) => setFormData({ ...formData, issueDate: date })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Expiry Date"
                    value={formData.expiryDate}
                    onChange={(date) => setFormData({ ...formData, expiryDate: date })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {!editingDocument && (
                <Box>
                  <input
                    accept="image/*,.pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="document-file-input"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setFormData({ ...formData, file });
                      }
                    }}
                  />
                  <label htmlFor="document-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      fullWidth
                    >
                      {formData.file ? formData.file.name : 'Select File'}
                    </Button>
                  </label>
                </Box>
              )}

              {editingDocument && (
                <Alert severity="info">
                  File replacement is not supported. Delete this document and create a new one to change the file.
                </Alert>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={uploading}
            >
              {uploading ? 'Saving...' : (editingDocument ? 'Update' : 'Add')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Document Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={handleCloseViewDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {viewingDocument && (
                  <>
                    <FileIcon />
                    <Typography variant="h6">
                      {viewingDocument.documentName || viewingDocument.file.name}
                    </Typography>
                    <Chip 
                      label={getDocumentTypeInfo(viewingDocument.documentType).label}
                      size="small"
                      sx={{
                        backgroundColor: `${getDocumentTypeInfo(viewingDocument.documentType).color}20`,
                        color: getDocumentTypeInfo(viewingDocument.documentType).color,
                      }}
                    />
                  </>
                )}
              </Box>
              <IconButton onClick={handleCloseViewDialog} edge="end">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: 'calc(100% - 64px)' }}>
            {viewingDocument && (
              <iframe
                src={viewingDocument.file.url}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title={viewingDocument.documentName || viewingDocument.file.name}
              />
            )}
          </DialogContent>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  );
};